import type { QueryClient } from '@tanstack/react-query';

/**
 * How long each key is allowed to lag behind a burst of socket events.
 *
 * Two windows, because the two things cost very different amounts. The lists
 * are a page of rows and come back in milliseconds, so theirs is short enough
 * to be invisible and long enough to collapse the pair of events every message
 * produces — `broadcastMessage` and `notifySessionsChanged` land together, and
 * without this each one refetched both lists. The count is an aggregate over
 * the workspace and costs two orders of magnitude more, so it waits longer.
 */
const LIST_COALESCE_MS = 250;
const UNREAD_COALESCE_MS = 2_000;

/**
 * Coalesces repeated invalidations of one key onto a trailing edge.
 *
 * Trailing rather than leading: the last event in a burst is the one whose
 * effect has to be shown, and a leading call would read the database before the
 * write that prompted it had settled.
 *
 * Module-level, so every caller in the app shares one timer per key. That is
 * the point — the whole problem is that several components invalidate the same
 * key in response to the same event.
 */
function coalesce(delayMs: number): (client: QueryClient, key: string) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return (client, key) => {
    if (timer) return;

    timer = setTimeout(() => {
      timer = null;
      void client.invalidateQueries({ queryKey: [key] });
    }, delayMs);
  };
}

const scheduleLists = coalesce(LIST_COALESCE_MS);
const scheduleUnread = coalesce(UNREAD_COALESCE_MS);

/**
 * What "the conversations changed" means to the cache.
 *
 * Two keys have to move together and they are not nested, so a bare
 * `invalidateQueries(['sessions'])` refreshes the lists and leaves the rail's
 * badge showing a number from before the message arrived.
 *
 * They are deliberately separate keys rather than one tree: the lists hold
 * pages of rows and are patched in place by `setQueriesData` when a
 * conversation is marked read, and a differently shaped entry underneath the
 * same prefix would be handed to that patcher as though it were a page.
 *
 * Both are coalesced. Every message the hub broadcasts reaches every agent's
 * socket, and each of them would otherwise refetch immediately —
 * `invalidateQueries` refetches an active observer whatever its staleTime, and
 * the rail is mounted above every screen, so the count is always active.
 * Measured on a 5,000-conversation workspace, eight of those recounts arriving
 * together took seconds and held every other query up behind them; and ten
 * messages in a row cost eighty list fetches before this.
 *
 * Call this instead of invalidating either key by hand, so the next thing that
 * depends on the session list is wired up in one place rather than in the nine
 * that used to spell this out.
 */
export function refreshSessions(client: QueryClient): void {
  scheduleLists(client, 'sessions');
  scheduleUnread(client, 'unread');
}

/** The badge alone — for the paths that change it without touching a list. */
export function refreshUnread(client: QueryClient): void {
  scheduleUnread(client, 'unread');
}
